import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { styled } from 'styled-components';
import { closestCenter, DndContext, DragOverlay } from '@dnd-kit/core';
import CompassHeader, { PageType } from 'Compass/components/CompassHeader';
import { useClusteringLabData } from '../hooks/useClusteringLabData';
import { useClusteringLabDnd } from '../hooks/useClusteringLabDnd';
import { useMarqueeSelection } from '../hooks/useMarqueeSelection';
import { usePhotoSelection } from '../hooks/usePhotoSelection';
import * as api from '../services/ClusteringLabService';
import ActionBar from '../components/ActionBar';
import Breadcrumb from '../components/Breadcrumb';
import DragOverlayContent from '../components/DragOverlayContent';
import DraggablePhotoTile from '../components/DraggablePhotoTile';
import DroppableGroupRow from '../components/DroppableGroupRow';
import DroppableUngroupedGrid from '../components/DroppableUngroupedGrid';
import MarqueeOverlay from '../components/MarqueeOverlay';
import { getGroupColor } from '../components/GroupPanel';
import DuplicatesPanel from '../components/DuplicatesPanel';
import ShareworthyPanel from '../components/ShareworthyPanel';

const PageWrapper = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  position: relative;
  padding: 16px;
`;

const Loading = styled.div`
  padding: 40px;
  text-align: center;
  color: #718096;
`;

const ErrorMsg = styled.div`
  padding: 20px;
  color: #c53030;
  background: #fee;
  border-radius: 5px;
  margin: 20px;
`;

const UserLidPrompt = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: 12px;
  font-size: 14px;
  color: #4a5568;
`;

const UserLidInput = styled.input`
  border: 1px solid #cbd5e0;
  border-radius: 6px;
  padding: 8px 14px;
  font-size: 14px;
  width: 200px;
  outline: none;

  &:focus {
    border-color: #3182ce;
    box-shadow: 0 0 0 1px #3182ce;
  }
`;

const GoBtn = styled.button`
  background: #3182ce;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 20px;
  font-size: 14px;
  cursor: pointer;

  &:hover {
    background: #2b6cb0;
  }
`;

const LoadMoreBtn = styled.button`
  display: block;
  margin: 12px auto;
  background: #edf2f7;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 8px 24px;
  font-size: 13px;
  color: #4a5568;
  cursor: pointer;

  &:hover {
    background: #e2e8f0;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ExportBtn = styled.button`
  background: #3182ce;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 6px 14px;
  cursor: pointer;
  font-size: 13px;
  margin-left: auto;

  &:hover {
    background: #2b6cb0;
  }
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 16px;
  border-bottom: 1px solid #e2e8f0;
  background: white;
  font-size: 13px;
  color: #718096;
`;

const Tabs = styled.div`
  display: flex;
  border-bottom: 1px solid #e2e8f0;
  background: white;
`;

const Tab = styled.button<{ $active: boolean }>`
  background: none;
  border: none;
  padding: 12px 24px;
  font-size: 14px;
  font-weight: ${(p) => (p.$active ? 600 : 400)};
  color: ${(p) => (p.$active ? '#2d3748' : '#718096')};
  border-bottom: 2px solid ${(p) => (p.$active ? '#3182ce' : 'transparent')};
  cursor: pointer;

  &:hover {
    color: #2d3748;
  }
`;

const GroupHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const GroupLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #2d3748;
  cursor: pointer;

  &:hover {
    color: #3182ce;
    text-decoration: underline;
  }
`;

const GroupCount = styled.span`
  font-size: 12px;
  color: #a0aec0;
`;

const DrillDownArrow = styled.span`
  font-size: 12px;
  color: #a0aec0;
  cursor: pointer;

  &:hover {
    color: #3182ce;
  }
`;

const GroupDeleteBtn = styled.button`
  background: none;
  border: none;
  color: #cbd5e0;
  cursor: pointer;
  font-size: 13px;
  padding: 2px 6px;
  margin-left: auto;

  &:hover {
    color: #e53e3e;
  }
`;

const GroupLabelInput = styled.input`
  font-size: 14px;
  font-weight: 600;
  border: 1px solid #3182ce;
  border-radius: 4px;
  padding: 2px 6px;
  outline: none;
  width: 200px;
`;

const PhotoStrip = styled.div`
  display: flex;
  gap: 6px;
  overflow-x: auto;
  padding-bottom: 4px;

  &::-webkit-scrollbar {
    height: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e0;
    border-radius: 2px;
  }
`;

const VisitClusterHeader = styled.div`
  width: 100%;
  font-size: 12px;
  color: #a0aec0;
  display: flex;
  gap: 8px;
  padding-bottom: 4px;
`;

function formatDateRange(startTs: string, endTs: string | null): string {
  const start = new Date(startTs);
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  const startStr = start.toLocaleDateString('en-US', opts);
  if (!endTs) return startStr;
  const end = new Date(endTs);
  if (start.toDateString() === end.toDateString()) return startStr;
  const endOpts: Intl.DateTimeFormatOptions =
    start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth()
      ? { day: 'numeric' }
      : { month: 'short', day: 'numeric' };
  return `${startStr} – ${end.toLocaleDateString('en-US', endOpts)}`;
}

export function getContiguousRange(photoIds: string[], allOrderedIds: string[]): string[] {
  const indices = photoIds
    .map((id) => allOrderedIds.indexOf(id))
    .filter((i) => i !== -1)
    .sort((a, b) => a - b);
  if (indices.length === 0) return [];
  return allOrderedIds.slice(indices[0], indices[indices.length - 1] + 1);
}

const ClusteringLabPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const paramLid = searchParams.get('user_lid');
  const userLid = paramLid ? parseInt(paramLid, 10) || null : null;
  const [inputValue, setInputValue] = useState('');

  type TabName = 'groups' | 'duplicates' | 'shareworthy';
  const rawTab = searchParams.get('tab');
  const tabFromUrl: TabName =
    rawTab === 'duplicates' ? 'duplicates' : rawTab === 'shareworthy' ? 'shareworthy' : 'groups';
  const setActiveTab = useCallback(
    (tab: TabName) => {
      const next = new URLSearchParams(searchParams);
      if (tab === 'groups') next.delete('tab');
      else next.set('tab', tab);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );
  const activeTab = tabFromUrl;

  const {
    photos,
    groups,
    memberships,
    visitClusters,
    loading,
    loadingMore,
    error,
    hasMore,
    totalCount,
    loadMore,
    setGroups,
    setMemberships,
  } = useClusteringLabData(userLid);

  const visitClusterMap = useMemo(
    () => new Map(visitClusters.map((vc) => [vc.visit_cluster_uuid, vc])),
    [visitClusters]
  );

  const [path, setPath] = useState<(string | null)[]>([null]);
  const currentGroupId = path[path.length - 1];

  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const editRef = useRef<HTMLInputElement>(null);

  const allVisiblePhotoIds = useMemo(() => {
    if (currentGroupId === null) {
      return photos.map((p) => p.photo_id);
    }
    const memberSet = new Set<string>();
    memberships.forEach((m) => {
      if (m.group_id === currentGroupId) memberSet.add(m.photo_id);
    });
    return photos.filter((p) => memberSet.has(p.photo_id)).map((p) => p.photo_id);
  }, [photos, memberships, currentGroupId]);

  const { selected, setSelected, handleClick, clearSelection } = usePhotoSelection(allVisiblePhotoIds);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const { marqueeRect, onContainerMouseDown } = useMarqueeSelection(scrollAreaRef, setSelected);

  const childGroups = useMemo(() => {
    return groups.filter((g) => g.parent_group_id === currentGroupId);
  }, [groups, currentGroupId]);

  const photoMap = useMemo(() => new Map(photos.map((p) => [p.photo_id, p])), [photos]);

  type Section =
    | { type: 'ungrouped'; photoIds: string[]; visitClusterUuid: string | null }
    | { type: 'group'; groupId: string; photoIds: string[] };

  const sections = useMemo(() => {
    const childGroupIds = new Set(childGroups.map((g) => g.group_id));

    const photoIdToGroup = new Map<string, string>();
    const groupPhotoIds = new Map<string, string[]>();
    memberships.forEach((m) => {
      if (childGroupIds.has(m.group_id)) {
        photoIdToGroup.set(m.photo_id, m.group_id);
      }
    });
    for (const photoId of allVisiblePhotoIds) {
      const groupId = photoIdToGroup.get(photoId);
      if (groupId) {
        const list = groupPhotoIds.get(groupId) || [];
        list.push(photoId);
        groupPhotoIds.set(groupId, list);
      }
    }

    const flushUngrouped = (result: Section[], ungrouped: string[]) => {
      if (ungrouped.length === 0) return;
      let currentVcUuid: string | null | undefined;
      let currentBatch: string[] = [];
      for (const id of ungrouped) {
        const photo = photoMap.get(id);
        const vcUuid = photo?.visit_cluster_uuid ?? null;
        if (currentVcUuid === undefined) {
          currentVcUuid = vcUuid;
        } else if (vcUuid !== currentVcUuid) {
          if (currentBatch.length > 0) {
            result.push({ type: 'ungrouped', photoIds: currentBatch, visitClusterUuid: currentVcUuid ?? null });
          }
          currentBatch = [];
          currentVcUuid = vcUuid;
        }
        currentBatch.push(id);
      }
      if (currentBatch.length > 0) {
        result.push({ type: 'ungrouped', photoIds: currentBatch, visitClusterUuid: currentVcUuid ?? null });
      }
    };

    const result: Section[] = [];
    const renderedGroups = new Set<string>();
    let currentUngrouped: string[] = [];

    for (const photoId of allVisiblePhotoIds) {
      const groupId = photoIdToGroup.get(photoId);
      if (!groupId) {
        currentUngrouped.push(photoId);
      } else if (!renderedGroups.has(groupId)) {
        flushUngrouped(result, currentUngrouped);
        currentUngrouped = [];
        result.push({ type: 'group', groupId, photoIds: groupPhotoIds.get(groupId) || [] });
        renderedGroups.add(groupId);
      }
    }
    flushUngrouped(result, currentUngrouped);
    return result;
  }, [allVisiblePhotoIds, memberships, childGroups, photoMap]);

  const repPhotoIds = useMemo(() => {
    return new Set(groups.map((g) => g.representative_photo_id).filter((id): id is string => id !== null));
  }, [groups]);

  const membershipByPhoto = useMemo(() => {
    const map = new Map<string, string>();
    const childGroupIds = new Set(childGroups.map((g) => g.group_id));
    memberships.forEach((m) => {
      if (childGroupIds.has(m.group_id)) {
        map.set(m.photo_id, m.group_id);
      }
    });
    return map;
  }, [memberships, childGroups]);

  const { sensors, activePhotoId, draggedPhotoIds, onDragStart, onDragEnd, onDragCancel } = useClusteringLabDnd({
    userLid,
    selected,
    membershipByPhoto,
    memberships,
    allVisiblePhotoIds,
    setMemberships,
    clearSelection,
  });

  const draggedSet = useMemo(() => new Set(draggedPhotoIds), [draggedPhotoIds]);

  const handleDrillDown = useCallback(
    (groupId: string) => {
      setPath((prev) => [...prev, groupId]);
      clearSelection();
    },
    [clearSelection]
  );

  const handleNavigate = useCallback(
    (depth: number) => {
      setPath((prev) => prev.slice(0, depth + 1));
      clearSelection();
    },
    [clearSelection]
  );

  const handleCreateGroup = useCallback(
    async (label: string) => {
      if (!userLid) return;
      const selectedIds = getContiguousRange(Array.from(selected), allVisiblePhotoIds);
      const result = await api.createGroup(userLid, label, selectedIds, currentGroupId);
      if (!result) return;
      setGroups((prev) => [...prev, result.group]);
      setMemberships((prev) => [
        ...prev,
        ...selectedIds.map((id) => ({ photo_id: id, group_id: result.group.group_id })),
      ]);
      clearSelection();
    },
    [userLid, selected, allVisiblePhotoIds, currentGroupId, setGroups, setMemberships, clearSelection]
  );

  const handleAddToGroup = useCallback(
    async (groupId: string) => {
      if (!userLid) return;
      const existingIds = memberships.filter((m) => m.group_id === groupId).map((m) => m.photo_id);
      const combined = [...new Set([...existingIds, ...Array.from(selected)])];
      const expanded = getContiguousRange(combined, allVisiblePhotoIds);
      const newIds = expanded.filter((id) => !existingIds.includes(id));
      if (newIds.length === 0) {
        clearSelection();
        return;
      }
      await api.assignMembers(userLid, groupId, newIds);
      setMemberships((prev) => [
        ...prev,
        ...newIds
          .filter((id) => !prev.some((m) => m.photo_id === id && m.group_id === groupId))
          .map((id) => ({ photo_id: id, group_id: groupId })),
      ]);
      clearSelection();
    },
    [userLid, selected, memberships, allVisiblePhotoIds, setMemberships, clearSelection]
  );

  const handleSetRepresentative = useCallback(async () => {
    if (!userLid || selected.size !== 1) return;
    const photoId = Array.from(selected)[0];
    const photoGroup = membershipByPhoto.get(photoId);
    if (photoGroup) {
      await api.updateGroup(userLid, photoGroup, { representative_photo_id: photoId });
      setGroups((prev) =>
        prev.map((g) => (g.group_id === photoGroup ? { ...g, representative_photo_id: photoId } : g))
      );
    } else if (currentGroupId !== null) {
      await api.updateGroup(userLid, currentGroupId, { representative_photo_id: photoId });
      setGroups((prev) =>
        prev.map((g) => (g.group_id === currentGroupId ? { ...g, representative_photo_id: photoId } : g))
      );
    }
    clearSelection();
  }, [userLid, selected, membershipByPhoto, currentGroupId, setGroups, clearSelection]);

  const handleMarkShareworthy = useCallback(
    async (isShareworthy: boolean) => {
      if (!userLid || selected.size === 0) return;
      const photoIds = Array.from(selected);
      await api.confirmShareworthy(userLid, photoIds, isShareworthy);
      clearSelection();
    },
    [userLid, selected, clearSelection]
  );

  const handleRemoveFromGroup = useCallback(async () => {
    if (!userLid) return;
    const selectedIds = Array.from(selected);
    const groupsToUnassign = new Map<string, string[]>();
    selectedIds.forEach((id) => {
      const groupId = membershipByPhoto.get(id);
      if (groupId) {
        const list = groupsToUnassign.get(groupId) || [];
        list.push(id);
        groupsToUnassign.set(groupId, list);
      }
    });

    for (const [groupId, ids] of groupsToUnassign) {
      await api.unassignMembers(userLid, groupId, ids);
    }

    setMemberships((prev) => {
      const toRemove = new Set(selectedIds);
      return prev.filter((m) => !toRemove.has(m.photo_id) || !groupsToUnassign.has(m.group_id));
    });
    clearSelection();
  }, [userLid, selected, membershipByPhoto, setMemberships, clearSelection]);

  const handleRenameGroup = useCallback(
    async (groupId: string) => {
      if (!userLid || !editValue.trim()) {
        setEditingGroupId(null);
        return;
      }
      await api.updateGroup(userLid, groupId, { label: editValue.trim() });
      setGroups((prev) => prev.map((g) => (g.group_id === groupId ? { ...g, label: editValue.trim() } : g)));
      setEditingGroupId(null);
    },
    [userLid, editValue, setGroups]
  );

  const handleDeleteGroup = useCallback(
    async (groupId: string) => {
      if (!userLid) return;
      await api.deleteGroup(userLid, groupId);
      setGroups((prev) => prev.filter((g) => g.group_id !== groupId && g.parent_group_id !== groupId));
      setMemberships((prev) => prev.filter((m) => m.group_id !== groupId));
    },
    [userLid, setGroups, setMemberships]
  );

  const handleExport = useCallback(async () => {
    if (!userLid) return;
    const data = await api.exportData(userLid);
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `clustering-labels-${userLid}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [userLid]);

  const canCreateGroup = useMemo(() => {
    return selected.size > 0 && Array.from(selected).every((id) => !membershipByPhoto.has(id));
  }, [selected, membershipByPhoto]);

  const canRemove = useMemo(() => {
    return Array.from(selected).some((id) => membershipByPhoto.has(id));
  }, [selected, membershipByPhoto]);

  const renderPhotoTile = (id: string) => {
    const photo = photoMap.get(id);
    return (
      <DraggablePhotoTile
        key={id}
        photoId={id}
        photoUrl={photo?.photo_url ?? ''}
        isSelected={selected.has(id)}
        isRepresentative={repPhotoIds.has(id)}
        onClick={handleClick}
        isDraggedAway={draggedSet.has(id)}
      />
    );
  };

  const groupMap = useMemo(() => new Map(groups.map((g) => [g.group_id, g])), [groups]);

  if (!userLid) {
    const handleGo = () => {
      const lid = parseInt(inputValue, 10);
      if (lid) setSearchParams({ user_lid: String(lid) });
    };
    return (
      <>
        <CompassHeader pageType={PageType.ClusteringLab} />
        <UserLidPrompt>
          <div>Enter a user_lid to load their photos:</div>
          <UserLidInput
            placeholder="e.g. 53"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleGo()}
            autoFocus
          />
          <GoBtn onClick={handleGo}>Load</GoBtn>
        </UserLidPrompt>
      </>
    );
  }

  return (
    <PageWrapper>
      <CompassHeader pageType={PageType.ClusteringLab} />
      <Tabs>
        <Tab $active={activeTab === 'groups'} onClick={() => setActiveTab('groups')}>
          Groups
        </Tab>
        <Tab $active={activeTab === 'duplicates'} onClick={() => setActiveTab('duplicates')}>
          Duplicates
        </Tab>
        <Tab $active={activeTab === 'shareworthy'} onClick={() => setActiveTab('shareworthy')}>
          Shareworthy
        </Tab>
      </Tabs>
      {activeTab === 'duplicates' ? (
        <DuplicatesPanel userLid={userLid} />
      ) : activeTab === 'shareworthy' ? (
        <ShareworthyPanel userLid={userLid} />
      ) : error ? (
        <ErrorMsg>{error}</ErrorMsg>
      ) : loading ? (
        <Loading>Loading photos...</Loading>
      ) : (
        <>
          <TopBar>
            <span>
              {photos.length} of {totalCount} photos loaded
            </span>
            <ExportBtn onClick={handleExport}>Export JSON</ExportBtn>
          </TopBar>
          <Breadcrumb path={path} groups={groups} onNavigate={handleNavigate} />
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            onDragCancel={onDragCancel}
          >
            <ScrollArea ref={scrollAreaRef} onMouseDown={onContainerMouseDown}>
              {marqueeRect && <MarqueeOverlay rect={marqueeRect} />}
              {sections.map((section, idx) => {
                if (section.type === 'ungrouped') {
                  const vc = section.visitClusterUuid ? visitClusterMap.get(section.visitClusterUuid) : null;
                  const sectionTimestamps = section.photoIds
                    .map((id) => photoMap.get(id)?.photo_ts)
                    .filter((ts): ts is string => ts !== null && ts !== undefined);
                  const sectionStartTs =
                    sectionTimestamps.length > 0 ? sectionTimestamps.reduce((a, b) => (a < b ? a : b)) : null;
                  const sectionEndTs =
                    sectionTimestamps.length > 1 ? sectionTimestamps.reduce((a, b) => (a > b ? a : b)) : null;
                  return (
                    <DroppableUngroupedGrid key={`ungrouped-${idx}`} index={idx}>
                      {(vc?.location || sectionStartTs) && (
                        <VisitClusterHeader>
                          {vc?.location && <span>{vc.location}</span>}
                          {sectionStartTs && <span>{formatDateRange(sectionStartTs, sectionEndTs)}</span>}
                        </VisitClusterHeader>
                      )}
                      {section.photoIds.map(renderPhotoTile)}
                    </DroppableUngroupedGrid>
                  );
                }
                const group = groupMap.get(section.groupId);
                if (!group) return null;
                const color = getGroupColor(group.group_id);
                return (
                  <DroppableGroupRow key={group.group_id} groupId={group.group_id} color={color}>
                    <GroupHeader>
                      {editingGroupId === group.group_id ? (
                        <GroupLabelInput
                          ref={editRef}
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onBlur={() => handleRenameGroup(group.group_id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleRenameGroup(group.group_id);
                            if (e.key === 'Escape') setEditingGroupId(null);
                          }}
                          autoFocus
                        />
                      ) : (
                        <GroupLabel
                          onClick={() => handleDrillDown(group.group_id)}
                          onDoubleClick={(e) => {
                            e.stopPropagation();
                            setEditingGroupId(group.group_id);
                            setEditValue(group.label);
                          }}
                        >
                          {group.label}
                        </GroupLabel>
                      )}
                      <GroupCount>({section.photoIds.length})</GroupCount>
                      <DrillDownArrow onClick={() => handleDrillDown(group.group_id)}>&#x276F;</DrillDownArrow>
                      <GroupDeleteBtn
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteGroup(group.group_id);
                        }}
                        title="Delete group"
                      >
                        ✕
                      </GroupDeleteBtn>
                    </GroupHeader>
                    <PhotoStrip>{section.photoIds.map(renderPhotoTile)}</PhotoStrip>
                  </DroppableGroupRow>
                );
              })}

              {hasMore && currentGroupId === null && (
                <LoadMoreBtn onClick={loadMore} disabled={loadingMore}>
                  {loadingMore ? 'Loading...' : 'Load More Photos'}
                </LoadMoreBtn>
              )}
            </ScrollArea>
            <DragOverlay dropAnimation={null}>
              {activePhotoId ? <DragOverlayContent photoIds={draggedPhotoIds} photoMap={photoMap} /> : null}
            </DragOverlay>
          </DndContext>
          <ActionBar
            selectedCount={selected.size}
            groups={groups}
            currentGroupId={currentGroupId}
            onCreateGroup={handleCreateGroup}
            canCreateGroup={canCreateGroup}
            onAddToGroup={handleAddToGroup}
            onSetRepresentative={handleSetRepresentative}
            onMarkShareworthy={handleMarkShareworthy}
            onRemoveFromGroup={handleRemoveFromGroup}
            canRemove={canRemove}
          />
        </>
      )}
    </PageWrapper>
  );
};

export default ClusteringLabPage;
